import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import HoverImagePill from "./HoverImagePill";
import ProgressBar from "./ProgressBar"; // <-- Import the new progress bar
import JSZip from "jszip";

ChartJS.register(ArcElement, Tooltip, Legend);

const getScoreText = (score) => {
  if (score <= 25) return "Covered Incidentally";
  if (score <= 50) return "Introduced";
  if (score <= 75) return "Reinforced";
  return "Emphasized";
};

const getTypeLabelAndIcon = (type) => {
  switch (type) {
    case "HTML":
      return { label: "WebPage", icon: "web.svg" };
    case "VIDEO":
      return { label: "Video", icon: "video.svg" };
    case "PDF":
    case "Canvas File PDF":
      return { label: "PDF", icon: "pdf.svg" };
    case "DOCX":
      return { label: "Word", icon: "word.svg" };
    case "PPT":
      return { label: "PPT", icon: "ppt.svg" };
    case "IMAGE":
      return { label: "Image", icon: "image.svg" };
    default:
      return { label: "File", icon: "file.svg" };
  }
};

function ScoringResults() {
  const [isKeywordsExpanded, setKeywordsExpanded] = useState(true);
  const [isConceptsExpanded, setConceptsExpanded] = useState(true);
  const [score, setScore] = useState("");
  const [results, setResults] = useState({});
  const [expanded, setExpanded] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [segmentPagination, setSegmentPagination] = useState({});
  const [selectedCourseIndex, setSelectedCourseIndex] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [selectedExportCourses, setSelectedExportCourses] = useState([]);
  const exportMenuRef = useRef(null);

  // New: track whether data is fully ready
  const [isDataReady, setIsDataReady] = useState(false);

  // Sorting states
  const [keywordsSortOrder, setKeywordsSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [conceptsSortOrder, setConceptsSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [materialsSortOrder, setMaterialsSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Export loading state
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState('');

  const location = useLocation();
  // Get parameters from location state, if available
  const locationState = location.state || { keywords: [], courses: [], concepts: [], frameworkName: null };
  const { keywords, courses, concepts, frameworkName } = locationState;

  const itemsPerPageForSegment = 5; 
  const itemsPerPage = 5;

  const handleSegmentPageChange = (segmentId, page, totalItems) => {
    const totalPages = Math.ceil(totalItems / itemsPerPageForSegment);
    if (page > 0 && page <= totalPages) {
      setSegmentPagination((prev) => ({
        ...prev,
        [segmentId]: page,
      }));
    }
  };

  const getCurrentPageForSegment = (segmentId) => segmentPagination[segmentId] || 1;

  const toggleExpand = (index) => {
    setExpanded((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handlePageChange = (page, totalPages) => {
    if (page > 0 && page <= totalPages) {
      setExpanded({});
      setCurrentPage(page);
    }
  };

  const toggleKeywords = () => setKeywordsExpanded(!isKeywordsExpanded);
  const toggleConcepts = () => setConceptsExpanded(!isConceptsExpanded);

  // Sorting functions
  const sortKeywords = (keywords) => {
    const entries = Object.entries(keywords);
    return entries.sort((a, b) => {
      const scoreA = a[1];
      const scoreB = b[1];
      return keywordsSortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB;
    });
  };

  const sortConcepts = (concepts) => {
    const entries = Object.entries(concepts);
    return entries.sort((a, b) => {
      const scoreA = a[1];
      const scoreB = b[1];
      return conceptsSortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB;
    });
  };

  const sortMaterials = (materials) => {
    const entries = Object.entries(materials);
    return entries.sort((a, b) => {
      const scoreA = a[1]?.overall_score || 0;
      const scoreB = b[1]?.overall_score || 0;
      return materialsSortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB;
    });
  };

  const toggleKeywordsSort = () => {
    setKeywordsSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const toggleConceptsSort = () => {
    setConceptsSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const toggleMaterialsSort = () => {
    setMaterialsSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Function to handle clicking outside of the export menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    }

    // Add event listener when the menu is shown
    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportMenu]);

  // Function to toggle course selection for export
  const toggleCourseForExport = (courseCode) => {
    setSelectedExportCourses(prev => {
      if (prev.includes(courseCode)) {
        return prev.filter(code => code !== courseCode);
      } else {
        return [...prev, courseCode];
      }
    });
  };

  // Function to check if a course has export data available
  const hasExportData = (courseCode) => {
    return results[courseCode] && 
           results[courseCode].excel_url && 
           results[courseCode].excel_url !== "";
  };

  // Function to handle export action
  const handleExport = async (type) => {
    let coursesToExport = [];
    
    if (type === 'all') {
      // Export all available courses
      coursesToExport = Object.keys(results).filter(hasExportData);
      if (coursesToExport.length === 0) {
        setExportStatus("No export data available for any course.");
        setTimeout(() => setExportStatus(''), 3000);
        return;
      }
    } else if (type === 'selected') {
      // Export only selected courses
      if (selectedExportCourses.length === 0) {
        setExportStatus("Please select at least one course to export.");
        setTimeout(() => setExportStatus(''), 3000);
        return;
      }
      coursesToExport = selectedExportCourses.filter(hasExportData);
      if (coursesToExport.length === 0) {
        setExportStatus("None of the selected courses have export data available.");
        setTimeout(() => setExportStatus(''), 3000);
        return;
      }
    }

    // Start loading state
    setIsExporting(true);
    setExportStatus(`Preparing to download ${coursesToExport.length} Excel file(s)...`);
    
    try {
      // Create a new JSZip instance
      const zip = new JSZip();
      
      // Download all Excel files and add them to the zip
      const downloadPromises = coursesToExport.map(async (courseCode) => {
        try {
          setExportStatus(`Downloading ${courseCode}...`);
          const response = await fetch(results[courseCode].excel_url);
          if (!response.ok) {
            throw new Error(`Failed to download ${courseCode}: ${response.status}`);
          }
          
          const blob = await response.blob();
          const fileName = `${courseCode}_export.xlsx`;
          zip.file(fileName, blob);
          
          return { courseCode, success: true };
        } catch (error) {
          console.error(`Error downloading ${courseCode}:`, error);
          return { courseCode, success: false, error: error.message };
        }
      });
      
      // Wait for all downloads to complete
      setExportStatus("Processing downloads...");
      const results_download = await Promise.all(downloadPromises);
      
      // Check if any downloads failed
      const failedDownloads = results_download.filter(result => !result.success);
      if (failedDownloads.length > 0) {
        const failedCourses = failedDownloads.map(result => result.courseCode).join(', ');
        console.warn(`Failed to download: ${failedCourses}`);
      }
      
      const successfulDownloads = results_download.filter(result => result.success);
      if (successfulDownloads.length === 0) {
        setExportStatus("Failed to download any Excel files. Please try again.");
        setTimeout(() => setExportStatus(''), 5000);
        setIsExporting(false);
        return;
      }
      
      // Generate the zip file
      setExportStatus("Creating zip file...");
      const zipBlob = await zip.generateAsync({ type: "blob" });
      
      // Create download link and trigger download
      const downloadLink = document.createElement('a');
      const url = URL.createObjectURL(zipBlob);
      downloadLink.href = url;
      
      // Create a meaningful filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = type === 'all' 
        ? `all_courses_export_${timestamp}.zip`
        : `selected_courses_export_${timestamp}.zip`;
      
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Clean up the URL object
      URL.revokeObjectURL(url);
      
      // Show success message
      if (failedDownloads.length > 0) {
        setExportStatus(`Download completed! ${successfulDownloads.length} file(s) downloaded successfully. ${failedDownloads.length} file(s) failed.`);
      } else {
        setExportStatus(`Download completed! ${successfulDownloads.length} Excel file(s) have been zipped and downloaded.`);
      }
      
      setTimeout(() => setExportStatus(''), 5000);
      
    } catch (error) {
      console.error("Error creating zip file:", error);
      setExportStatus("An error occurred while preparing the download. Please try again.");
      setTimeout(() => setExportStatus(''), 5000);
    }
    
    // End loading state
    setIsExporting(false);
    
    // Close the export menu
    setShowExportMenu(false);
  };

  // Function to fetch score data with a job ID
  const fetchScoreData = async (jobId) => {
    try {
      const pollHeaders = new Headers();
      pollHeaders.append("Content-Type", "application/json");

      const pollUrl = "https://be2.aristai.io/api/search/v2_get_score";
      const pollBody = JSON.stringify({ job_id: jobId });
      const pollOptions = {
        method: "POST",
        headers: pollHeaders,
        body: pollBody,
        redirect: "follow",
      };

      const pollResponse = await fetch(pollUrl, pollOptions);

      if (pollResponse.status === 404) {
        console.log("Result not found (404)");
        return false;
      }

      if (pollResponse.ok) {
        const pollData = await pollResponse.json();

        // Check for errors
        const hasErrorKey = Object.values(pollData).some(value => {
          return (
            value &&
            typeof value === "object" &&
            "error" in value &&
            value.error
          );
        });

        if (hasErrorKey) {
          console.log("Error in response data");
          return false;
        }

        // Store the final data
        setResults(pollData);
        setIsDataReady(true);
        return true;
      } else {
        console.error(`Polling returned status: ${pollResponse.status}`);
        return false;
      }
    } catch (err) {
      console.error("Error fetching score data:", err);
      return false;
    }
  };

  useEffect(() => {
    let pollInterval;
    let isMounted = true; // Flag to track if component is mounted
    
    // First check if we have a job_id in the URL
    const urlJobId = searchParams.get('job_id');
    
    const initializeData = async () => {
      if (urlJobId) {
        // We have a job ID in the URL, try to fetch results directly
        setJobId(urlJobId);
        await fetchScoreData(urlJobId);
      } else if (keywords.length > 0 && courses.length > 0) {
        // No job ID in URL but we have search parameters from location state, create a new job
        await createNewJob();
      }
    };
    
    initializeData();
    
    // Function to create a new scoring job
    async function createNewJob() {
      try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
  
        const url = "https://be2.aristai.io/api/search/v2_generate_score";
        
        // Prepare the request body
        const requestBody = {
          Search_Keywords_List: keywords,
          Course_Code: courses,
          Search_Concept_List: concepts,
          School: "UIUC",
        };
        
        // Add additional_info if frameworkName is available
        if (frameworkName) {
          requestBody.additional_info = {
            learning_goal_description: frameworkName
          };
        }
        
        const raw = JSON.stringify(requestBody);
  
        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow",
        };
  
        const response = await fetch(url, requestOptions);
        if (!response.ok) {
          throw new Error(`Generate score request failed: ${response.status}`);
        }
  
        const data = await response.json();
        
        // Only update state if component is still mounted
        if (!isMounted) return;
        
        // Set the job ID and update the URL
        const newJobId = data.job_id;
        setJobId(newJobId);
        
        // Update URL with job_id parameter without triggering a re-render
        const newSearchParams = new URLSearchParams();
        newSearchParams.set('job_id', newJobId);
        setSearchParams(newSearchParams, { replace: true });
  
        // Start polling for results
        pollInterval = setInterval(async () => {
          try {
            if (!isMounted) {
              clearInterval(pollInterval);
              return;
            }
            
            const success = await fetchScoreData(newJobId);
            if (success) {
              // If we successfully got the data, stop polling
              clearInterval(pollInterval);
              pollInterval = null;
            }
          } catch (pollErr) {
            console.error("Error while polling for score data:", pollErr);
          }
        }, 10000);
      } catch (err) {
        console.error("Error creating job:", err);
      }
    }
  
    // Cleanup: on component unmount, clear any pending intervals
    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If we haven't received the results yet, show the progress bar
  if (!Object.keys(results).length) {
    // The progress bar won't exceed 90% unless isDataReady = true
    return <ProgressBar isDataReady={isDataReady} />;
  }

  // Otherwise, we have data to display:
  const courseKeys = Object.keys(results);
  const currentCourseKey = courseKeys[selectedCourseIndex];
  const currentCourseData = results[currentCourseKey] || {};

  const currentScore = Math.floor(currentCourseData.overall_score || 0);
  const materialEntries = sortMaterials(currentCourseData.materials || {});
  const totalItems = materialEntries.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = materialEntries.slice(startIndex, startIndex + itemsPerPage);

  const data = {
    datasets: [
      {
        data: [currentScore, 100 - currentScore],
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return "#56539D";
          }

          const gradient = ctx.createLinearGradient(
            chartArea.left,
            0,
            chartArea.right,
            0
          );
          gradient.addColorStop(0, "#EC5D10");
          gradient.addColorStop(0.3, "#F7AC4C");
          gradient.addColorStop(1, "#89BD00");
          return [gradient, "#E0E0E0"];
        },
        borderWidth: 0,
      },
    ],
  };

  const options = {
    rotation: -90,
    circumference: 180,
    cutout: "80%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  return (
    <div className="main-body">
      {/* Add CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Header Section */}
      <div className="top-bar">
        <img className="logo" src="logo.svg" alt="logo" />
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ position: 'relative' }} ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#56539D",
                color: "white",
                borderRadius: "5px",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              <span>Export</span>
              <span style={{ fontSize: '10px' }}>▼</span>
            </button>
            
            {showExportMenu && (
              <div 
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  zIndex: 10,
                  minWidth: '200px',
                  padding: '10px'
                }}
              >
                {/* Export Status Display */}
                {(isExporting || exportStatus) && (
                  <div style={{ 
                    marginBottom: '10px', 
                    padding: '8px', 
                    backgroundColor: isExporting ? '#f0f8ff' : exportStatus.includes('completed') ? '#f0fff0' : '#fff8f0',
                    border: `1px solid ${isExporting ? '#87ceeb' : exportStatus.includes('completed') ? '#90ee90' : '#ffd700'}`,
                    borderRadius: '4px',
                    fontSize: '12px',
                    textAlign: 'center'
                  }}>
                    {isExporting && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                        <div style={{ 
                          width: '12px', 
                          height: '12px', 
                          border: '2px solid #f3f3f3',
                          borderTop: '2px solid #56539D',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <span>{exportStatus}</span>
                      </div>
                    )}
                    {!isExporting && exportStatus && (
                      <span>{exportStatus}</span>
                    )}
                  </div>
                )}
                
                <div style={{ borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '10px' }}>
                  <button
                    onClick={() => handleExport('all')}
                    style={{
                      padding: "8px 15px",
                      backgroundColor: isExporting ? "#ccc" : "#56539D",
                      color: "white",
                      borderRadius: "5px",
                      border: "none",
                      cursor: isExporting ? "not-allowed" : "pointer",
                      width: '100%',
                      marginBottom: '5px'
                    }}
                    disabled={!Object.keys(results).some(hasExportData) || isExporting}
                  >
                    Export All Available
                  </button>
                </div>
                
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Select Courses:</div>
                  {courseKeys.map(courseCode => (
                    <div key={courseCode} style={{ marginBottom: '5px', display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        id={`export-${courseCode}`}
                        checked={selectedExportCourses.includes(courseCode)}
                        onChange={() => toggleCourseForExport(courseCode)}
                        disabled={!hasExportData(courseCode)}
                        style={{ marginRight: '5px' }}
                      />
                      <label 
                        htmlFor={`export-${courseCode}`}
                        style={{ 
                          color: hasExportData(courseCode) ? 'black' : '#999',
                          cursor: hasExportData(courseCode) ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {courseCode} {!hasExportData(courseCode) && "(No data)"}
                      </label>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={() => handleExport('selected')}
                  style={{
                    padding: "8px 15px",
                    backgroundColor: (selectedExportCourses.length > 0 && !isExporting) ? "#56539D" : "#ccc",
                    color: "white",
                    borderRadius: "5px",
                    border: "none",
                    cursor: (selectedExportCourses.length > 0 && !isExporting) ? "pointer" : "not-allowed",
                    width: '100%'
                  }}
                  disabled={selectedExportCourses.length === 0 || isExporting}
                >
                  Export Selected
                </button>
              </div>
            )}
          </div>
          
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "10px 20px",
              backgroundColor: "white",
              color: "black",
              borderRadius: "5px",
              border: "1px solid #5C5C5C",
              cursor: "pointer",
            }}
          >
            Go back
          </button>
        </div>
      </div>

      {/* Tabs for multiple courses */}
      

      <div className="score-content">
      {courseKeys.length > 1 && (
        <div className="tabs" style={{ 
          display: "flex", 
          gap: "10px", 
          marginBottom: "20px", 
          marginLeft: "92px", 
          marginRight: "92px" 
        }}>
          {courseKeys.map((courseCode, i) => (
            <button
              key={courseCode}
              onClick={() => {
                setSelectedCourseIndex(i);
                setExpanded({});
                setCurrentPage(1);
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: selectedCourseIndex === i ? "#56539D" : "#fff",
                color: selectedCourseIndex === i ? "#fff" : "#000",
                border: "1px solid #5C5C5C",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              {courseCode}
            </button>
          ))}
        </div>
      )}
        <div className="content-header">
          Scoring Results for {currentCourseKey}
        </div>

        {/* Semi-Donut Chart */}
        <div style={{ position: "relative", width: "200px", margin: "0 auto" }}>
          <Doughnut data={data} options={options} />
          <div
            style={{
              position: "absolute",
              top: "60%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "36px", color: "#56539D" }}>{currentScore}</div>
            <div style={{ fontSize: "18px", color: "#56539D" }}>{getScoreText(currentScore)}</div>
          </div>
        </div>

        <div className="total-score">
          Total Coverage Score
        </div>

        {/* Keywords Section */}
        {currentCourseData.keywords && Object.keys(currentCourseData.keywords).length > 0 && (
          <div className="keywords-section">
            <div
              className="keywords-header"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <img src="keywords.svg" alt="keywords icon" style={{ marginRight: "8px" }} />
                <div style={{ fontSize: "18px", fontWeight: "bold" }}>Keywords</div>
              </div>
              <button onClick={toggleKeywords} className="keyword-expand-btn">
                Total score for all keywords:{" "}
                <span className="score-number">
                  {currentCourseData.keywords_overall_score
                    ? Math.floor(currentCourseData.keywords_overall_score)
                    : 0}
                </span>
                <img
                  src={isKeywordsExpanded ? "collapse.svg" : "expand.svg"}
                  alt={isKeywordsExpanded ? "Collapse" : "Expand"}
                />
              </button>
            </div>
            {isKeywordsExpanded && currentCourseData.keywords && (
              <table
                style={{
                  width: "calc(100% + 60px)",
                  marginTop: "25px",
                  borderCollapse: "collapse",
                  borderSpacing: "0 10px",
                  marginLeft: "-30px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: "left",
                      fontSize: "14px",
                      color: "#525252",
                      borderBottom: "2px solid #ccc",
                      height: "50px",
                    }}
                  >
                    <th style={{ padding: "0 30px", width: "320px" }}>KEYWORDS</th>
                    <th style={{ position: "relative", left: "-20px", width: "300px" }}>
                      COVERAGE LEVEL
                    </th>
                    <th style={{ position: "relative", left: "-30px", textAlign: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                        COURSE SCORE
                        <button
                          onClick={toggleKeywordsSort}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "2px",
                            display: "flex",
                            alignItems: "center"
                          }}
                          title={`Sort ${keywordsSortOrder === 'desc' ? 'ascending' : 'descending'}`}
                        >
                          <span style={{ fontSize: "12px", color: "#525252" }}>
                            {keywordsSortOrder === 'desc' ? '↓' : '↑'}
                          </span>
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortKeywords(currentCourseData.keywords).map(([keyword, kwScore], index) => {
                    const filledBoxes = Math.min(4, Math.ceil(kwScore / 25));
                    const totalBoxes = 4;

                    return (
                      <tr
                        key={index}
                        style={{
                          fontSize: "16px",
                          color: "#181818",
                          borderBottom:
                            index === Object.keys(currentCourseData.keywords).length - 1
                              ? "none"
                              : "1px solid #ccc",
                          height: "70px",
                          fontWeight: "bold",
                        }}
                      >
                        <td style={{ padding: "0 30px" }}>{keyword}</td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "10px",
                              position: "relative",
                              left: "-110px",
                              marginTop: "5px",
                            }}
                          >
                            <div style={{ display: "flex", gap: "5px" }}>
                              {Array.from({ length: totalBoxes }, (_, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    width: "15px",
                                    height: "15px",
                                    borderRadius: "3px",
                                    backgroundColor:
                                      idx < filledBoxes ? "#56539D" : "#E0E0E0",
                                  }}
                                ></div>
                              ))}
                            </div>
                            <div
                              style={{
                                fontSize: "16px",
                                color: "#525252",
                                marginLeft: "10px",
                              }}
                            >
                              {getScoreText(kwScore)}
                            </div>
                          </div>
                        </td>
                        <td>{Math.floor(kwScore)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Concepts Section */}
        {currentCourseData.concepts && Object.keys(currentCourseData.concepts).length > 0 && (
          <div className="concepts-section">
            <div
              className="concepts-header"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <img src="light.svg" alt="concepts icon" style={{ marginRight: "8px" }} />
                <div style={{ fontSize: "18px", fontWeight: "bold" }}>Concepts</div>
              </div>
              <button onClick={toggleConcepts} className="concept-expand-btn">
                Total score for all concepts:{" "}
                <span className="score-number">
                  {currentCourseData.concepts_overall_score
                    ? Math.floor(currentCourseData.concepts_overall_score)
                    : 0}
                </span>
                <img
                  src={isConceptsExpanded ? "collapse.svg" : "expand.svg"}
                  alt={isConceptsExpanded ? "Collapse" : "Expand"}
                />
              </button>
            </div>
            {isConceptsExpanded && currentCourseData.concepts && (
              <table
                style={{
                  width: "calc(100% + 60px)",
                  marginTop: "25px",
                  borderCollapse: "collapse",
                  borderSpacing: "0 10px",
                  marginLeft: "-30px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: "left",
                      fontSize: "14px",
                      color: "#525252",
                      borderBottom: "2px solid #ccc",
                      height: "50px",
                    }}
                  >
                    <th style={{ padding: "0 30px", width: "320px" }}>CONCEPTS</th>
                    <th style={{ position: "relative", left: "-20px", width: "300px" }}>
                      COVERAGE LEVEL
                    </th>
                    <th style={{ position: "relative", left: "-30px", textAlign: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                        COURSE SCORE
                        <button
                          onClick={toggleConceptsSort}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "2px",
                            display: "flex",
                            alignItems: "center"
                          }}
                          title={`Sort ${conceptsSortOrder === 'desc' ? 'ascending' : 'descending'}`}
                        >
                          <span style={{ fontSize: "12px", color: "#525252" }}>
                            {conceptsSortOrder === 'desc' ? '↓' : '↑'}
                          </span>
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortConcepts(currentCourseData.concepts).map(([concept, cScore], index) => {
                    const filledBoxes = Math.min(4, Math.ceil(cScore / 25));
                    const totalBoxes = 4;

                    return (
                      <tr
                        key={index}
                        style={{
                          fontSize: "16px",
                          color: "#181818",
                          borderBottom:
                            index === Object.keys(currentCourseData.concepts).length - 1
                              ? "none"
                              : "1px solid #ccc",
                          height: "70px",
                          fontWeight: "bold",
                        }}
                      >
                        <td style={{ padding: "0 30px" }}>{concept}</td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "10px",
                              position: "relative",
                              left: "-110px",
                              marginTop: "5px",
                            }}
                          >
                            <div style={{ display: "flex", gap: "5px" }}>
                              {Array.from({ length: totalBoxes }, (_, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    width: "15px",
                                    height: "15px",
                                    borderRadius: "3px",
                                    backgroundColor:
                                      idx < filledBoxes ? "#2F74D4" : "#E0E0E0",
                                  }}
                                ></div>
                              ))}
                            </div>
                            <div
                              style={{
                                fontSize: "16px",
                                color: "#525252",
                                marginLeft: "10px",
                              }}
                            >
                              {getScoreText(cScore)}
                            </div>
                          </div>
                        </td>
                        <td>{Math.floor(cScore)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Materials Section */}
        <div className="concepts-section">
          <div
            className="concepts-header"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <img src="materials.svg" alt="materials icon" style={{ marginRight: "8px" }} />
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                Related Course Materials
              </div>
            </div>
          </div>

          <table
            style={{
              width: "calc(100% + 60px)",
              marginTop: "25px",
              borderCollapse: "collapse",
              borderSpacing: "0 10px",
              marginLeft: "-30px",
            }}
          >
            <thead>
              <tr
                style={{
                  textAlign: "left",
                  fontSize: "14px",
                  color: "#525252",
                  borderBottom: "2px solid #ccc",
                  height: "50px",
                }}
              >
                <th style={{ position: "relative", right: "-15px" }}>COURSE MATERIAL NAME</th>
                <th style={{ position: "relative", right: "-15px" }}>CONTENT TYPE</th>
                <th style={{ position: "relative", right: "-25px" }}>COVERAGE LEVEL</th>
                <th style={{ position: "relative", right: "-10px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                    MATERIAL SCORE
                    <button
                      onClick={toggleMaterialsSort}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "2px",
                        display: "flex",
                        alignItems: "center"
                      }}
                      title={`Sort ${materialsSortOrder === 'desc' ? 'ascending' : 'descending'}`}
                    >
                      <span style={{ fontSize: "12px", color: "#525252" }}>
                        {materialsSortOrder === 'desc' ? '↓' : '↑'}
                      </span>
                    </button>
                  </div>
                </th>
                <th style={{ position: "relative", right: "-0px" }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {paginatedResults.map(([materialId, materialData], index) => {
                const isExpanded = expanded[startIndex + index];

                const getCoverageText = (sc) => {
                  if (sc > 75) return "Emphasized";
                  if (sc > 50) return "Reinforced";
                  if (sc > 25) return "Introduced";
                  return "Covered Incidentally";
                };

                const filledBoxes = (sc) => Math.min(4, Math.ceil(sc / 25));
                const totalBoxes = 4;

                const renderScoreBoxes = (sc) => (
                  <div style={{ display: "flex", gap: "5px" }}>
                    {Array.from({ length: totalBoxes }, (_, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: "15px",
                          height: "15px",
                          borderRadius: "3px",
                          backgroundColor: idx < filledBoxes(sc) ? "#3A8452" : "#E0E0E0",
                        }}
                      ></div>
                    ))}
                  </div>
                );

                return (
                  <React.Fragment key={materialId}>
                    <tr>
                      <td>
                        <div
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "normal",
                            maxWidth: "250px",
                            textAlign: "center",
                          }}
                        >
                          {(() => {
                            // Check if info_dict is a list, and use the first element if it is
                            const infoDict = Array.isArray(materialData?.info_dict) 
                              ? materialData?.info_dict[0] 
                              : materialData?.info_dict;
                            
                            return infoDict?.Title || "Untitled";
                          })()}
                        </div>
                      </td>
                      <td>
                        <div>
                          {(() => {
                            // Check if info_dict is a list, and use the first element if it is
                            const infoDict = Array.isArray(materialData?.info_dict) 
                              ? materialData?.info_dict[0] 
                              : materialData?.info_dict;
                            
                            if (!infoDict || !infoDict["Material Type"]) return null;
                            
                            const { label, icon } = getTypeLabelAndIcon(
                              infoDict["Material Type"]
                            );
                            return (
                              <>
                                <img
                                  src={icon}
                                  alt={label}
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    marginRight: "5px",
                                    verticalAlign: "middle",
                                  }}
                                />
                                <span>{label}</span>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      <td>
                        <div>
                          {renderScoreBoxes(materialData?.overall_score)}
                          <div>{getCoverageText(materialData?.overall_score)}</div>
                        </div>
                      </td>
                      <td>
                        <div>{Math.floor(materialData?.overall_score)}</div>
                      </td>
                      <td>
                        <button
                          onClick={() => toggleExpand(startIndex + index)}
                          className={`action-button-score ${
                            expanded[startIndex + index] ? "expanded" : ""
                          }`}
                        >
                          <img
                            style={{ position: "relative", right: "-15px" }}
                            src={expanded[startIndex + index] ? "collapse.svg" : "expand.svg"}
                            alt={expanded[startIndex + index] ? "Hide" : "Show"}
                          />
                        </button>
                      </td>
                    </tr>

                    {isExpanded && materialData.segments && (
                      <tr className="expand-section">
                        <td colSpan={5} style={{ backgroundColor: "#f9f9f9" }}>
                          {(() => {
                            const segmentEntries = Object.entries(materialData.segments);
                            const segmentTotalItems = segmentEntries.length;
                            const segmentTotalPages = Math.ceil(
                              segmentTotalItems / itemsPerPageForSegment
                            );
                            const segmentCurrentPage = getCurrentPageForSegment(materialId);
                            const segmentStartIndex =
                              (segmentCurrentPage - 1) * itemsPerPageForSegment;
                            const segmentPaginatedEntries = segmentEntries.slice(
                              segmentStartIndex,
                              segmentStartIndex + itemsPerPageForSegment
                            );

                            return (
                              <>
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    backgroundColor: "#fff",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <thead>
                                    <tr
                                      style={{
                                        backgroundColor: "#ECF5EE",
                                        textAlign: "center",
                                        height: "50px",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "15px",
                                          fontSize: "16px",
                                          fontWeight: "bold",
                                          color: "#2A6DCB",
                                          border: "1px solid #e0e0e0",
                                          textAlign: "center",
                                        }}
                                      >
                                        Section
                                      </th>
                                      <th
                                        style={{
                                          padding: "15px",
                                          fontSize: "16px",
                                          fontWeight: "bold",
                                          color: "#2A6DCB",
                                          border: "1px solid #e0e0e0",
                                          textAlign: "center",
                                        }}
                                      >
                                        <div style={{ textAlign: "center" }}>
                                          <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                                            Keywords
                                          </div>
                                          <div style={{ fontSize: "14px", color: "#666" }}>
                                            {`Score of the material: ${Math.floor(
                                              materialData?.keywords_overall_score
                                            )}`}
                                          </div>
                                        </div>
                                      </th>
                                      <th
                                        style={{
                                          padding: "15px",
                                          fontSize: "16px",
                                          fontWeight: "bold",
                                          color: "#2A6DCB",
                                          border: "1px solid #e0e0e0",
                                          textAlign: "center",
                                        }}
                                      >
                                        <div style={{ textAlign: "center" }}>
                                          <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                                            Concepts
                                          </div>
                                          <div style={{ fontSize: "14px", color: "#666" }}>
                                            {`Score of the material: ${Math.floor(
                                              materialData?.concepts_overall_score
                                            )}`}
                                          </div>
                                        </div>
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {segmentPaginatedEntries.map(([segId, segmentData]) => (
                                      <tr
                                        key={segId}
                                        style={{ height: "auto", textAlign: "center" }}
                                      >
                                        <td
                                          style={{
                                            padding: "15px",
                                            width: "30%",
                                            backgroundColor: "#ECF5EE",
                                            border: "1px solid #e0e0e0",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <a
                                            href={segmentData.material_link_with_timestamp}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              textDecoration: "none",
                                              color: "#2A6DCB",
                                              fontSize: "16px",
                                              fontWeight: "bold",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {segId}
                                            <img
                                              src="open_link.svg"
                                              alt="open link"
                                              style={{ width: "14px", height: "14px" }}
                                            />
                                          </a>
                                        </td>
                                        <td
                                          style={{
                                            padding: "15px",
                                            width: "35%",
                                            backgroundColor: "#ffffff",
                                            border: "1px solid #e0e0e0",
                                            verticalAlign: "middle",
                                            textAlign: "center",
                                          }}
                                        >
                                          {segmentData.keyword_list.length > 0
                                            ? segmentData.keyword_list.map((keyword, i) => {
                                                // Pull screenshot for this keyword if it exists
                                                const keywordScreenshotUrl =
                                                  segmentData.screenshot_dict?.keyword_dict?.[
                                                    keyword
                                                  ];
                                                return (
                                                  <HoverImagePill
                                                    key={i}
                                                    label={keyword}
                                                    imageUrl={keywordScreenshotUrl}
                                                  />
                                                );
                                              })
                                            : null}
                                        </td>
                                        <td
                                          style={{
                                            padding: "15px",
                                            width: "35%",
                                            backgroundColor: "#ffffff",
                                            border: "1px solid #e0e0e0",
                                            verticalAlign: "middle",
                                            textAlign: "center",
                                          }}
                                        >
                                          {segmentData.concept_list.length > 0
                                            ? segmentData.concept_list.map((concept, j) => {
                                                // Pull screenshot for this concept if it exists
                                                const conceptScreenshotUrl =
                                                  segmentData.screenshot_dict?.concept_dict?.[
                                                    concept
                                                  ];
                                                return (
                                                  <HoverImagePill
                                                    key={j}
                                                    label={concept}
                                                    imageUrl={conceptScreenshotUrl}
                                                  />
                                                );
                                              })
                                            : null}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                {/* Segments Pagination Controls */}
                                {segmentTotalPages > 1 && (
                                  <tr
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <td colSpan={5} style={{ textAlign: "center", padding: "10px 0" }}>
                                      <button
                                        onClick={() =>
                                          handleSegmentPageChange(
                                            materialId,
                                            segmentCurrentPage - 1,
                                            segmentTotalItems
                                          )
                                        }
                                        disabled={segmentCurrentPage === 1}
                                        style={{
                                          padding: "8px 12px",
                                          marginRight: "10px",
                                          backgroundColor:
                                            segmentCurrentPage === 1 ? "#ccc" : "#56539D",
                                          color: "white",
                                          border: "none",
                                          borderRadius: "4px",
                                          cursor:
                                            segmentCurrentPage === 1 ? "not-allowed" : "pointer",
                                        }}
                                      >
                                        Previous
                                      </button>

                                      <span style={{ fontSize: "16px", margin: "0 10px" }}>
                                        Page {segmentCurrentPage} of {segmentTotalPages}
                                      </span>

                                      <button
                                        onClick={() =>
                                          handleSegmentPageChange(
                                            materialId,
                                            segmentCurrentPage + 1,
                                            segmentTotalItems
                                          )
                                        }
                                        disabled={segmentCurrentPage === segmentTotalPages}
                                        style={{
                                          padding: "8px 12px",
                                          marginLeft: "10px",
                                          backgroundColor:
                                            segmentCurrentPage === segmentTotalPages
                                              ? "#ccc"
                                              : "#56539D",
                                          color: "white",
                                          border: "none",
                                          borderRadius: "4px",
                                          cursor:
                                            segmentCurrentPage === segmentTotalPages
                                              ? "not-allowed"
                                              : "pointer",
                                        }}
                                      >
                                        Next
                                      </button>
                                    </td>
                                  </tr>
                                )}
                              </>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Pagination for the Materials */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "150px", alignItems: "center" }}>
            <button
              onClick={() => handlePageChange(currentPage - 1, totalPages)}
              disabled={currentPage === 1}
              style={{
                padding: "8px 12px",
                marginRight: "10px",
                backgroundColor: currentPage === 1 ? "#ccc" : "#56539D",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: "16px", margin: "0 10px", fontWeight: "bold" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1, totalPages)}
              disabled={currentPage === totalPages}
              style={{
                padding: "8px 12px",
                marginLeft: "10px",
                backgroundColor: currentPage === totalPages ? "#ccc" : "#56539D",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        </div>

        <div className="button-white"></div>
      </div>
    </div>
  );
}

export default ScoringResults;
